import {Component, inject, OnInit} from '@angular/core';
import {forkJoin} from "rxjs";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {ShiftHttpService} from "../_services/shift-http.service";
import {EmployeeEventResponseSummary} from "../../company/_models/employee/employee-event-response-summary";
import {AlertLevel} from "../../../enums/alert-level";
import {NgSelectModule} from "@ng-select/ng-select";
import {FormsModule} from "@angular/forms";
import {CommonModule} from "@angular/common";
import {NextShiftItemComponent} from "../_components/next-shift-item/next-shift-item.component";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {
  addCalendarDays,
  addCalendarMonths,
  companyToday,
  DayKey,
  endOfMonth,
  isInCompanyDayRange,
  mondayBasedWeekday,
  startOfMonth
} from "../../../_shared/time/company-day";

/** The quick-select chips of the Arbeitszeiten filter. */
export type ShiftRangePreset = 'all' | 'today' | 'week' | 'month' | 'lastMonth' | 'upcoming';

/**
 * The `[from, to]` day keys a preset selects, relative to `today` (a company-local day key). An empty string is an
 * open bound. Weeks start on Monday. Pure calendar arithmetic — no `Date`, no zone.
 */
export function shiftPresetRange(preset: ShiftRangePreset, today: DayKey): { from: DayKey | ''; to: DayKey | '' } {
  switch (preset) {
    case 'all':
      return {from: '', to: ''};
    case 'today':
      return {from: today, to: today};
    case 'week': {
      const monday = addCalendarDays(today, -mondayBasedWeekday(today));
      return {from: monday, to: addCalendarDays(monday, 6)};
    }
    case 'month':
      return {from: startOfMonth(today), to: endOfMonth(today)};
    case 'lastMonth': {
      const previous = addCalendarMonths(today, -1);
      return {from: previous, to: endOfMonth(previous)};
    }
    case 'upcoming':
      return {from: today, to: ''};
  }
}

/**
 * Employee panel "Arbeitszeiten": the employee's worked shifts, filtered by a day range, a search term and a Bereich.
 *
 * The whole list is loaded ONCE and filtered locally — the server cross-loads every event response on each call
 * anyway, so a round-trip per filter click would only repeat that work (#645).
 *
 * Day rule (#645): a shift belongs to the calendar day of its EVENT start in the COMPANY's time zone
 * (`CompanyTimeZoneService`), and the range `[Von, Bis]` includes both days. Not the shift's own start, not an
 * overlap with the range, and never the browser's zone — otherwise an event starting 31.07. 21:30 and ending
 * 01.08. 06:00 shows up under August, or a viewer abroad sees a different month than the office.
 */
@Component({
  selector: 'app-shifts',
  imports: [
    CommonModule,
    NgSelectModule,
    FormsModule,
    NextShiftItemComponent
  ],
  templateUrl: './shifts.component.html',
  styleUrl: './shifts.component.scss'
})
export class ShiftsComponent implements OnInit {
  private readonly gas = inject(GlobalAlertService);
  private readonly shiftHttp = inject(ShiftHttpService);
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  ngOnInit(): void {
    this.load();
  }

  shifts: EmployeeEventResponseSummary[] | undefined | null = undefined;

  load() {
    // The zone and the list arrive together, so the first filter pass already uses the company's zone.
    // `CompanyTimeZoneService.load()` never errors (it falls back to the server default), so only the list can fail.
    forkJoin({zone: this.companyTimeZone.load(), overview: this.shiftHttp.getShiftOverview()})
      .withLoadingIndicator()
      .subscribe({
        next: ({overview}) => {
          this.shifts = overview.employeeEventResponses.reverse();
          // A chip clicked before the zone was known computed "today" from the default zone; redo it.
          if (this.activePreset) this.applyPreset(this.activePreset);
          this.sort();
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Schichten", AlertLevel.error);
          this.shifts = null;
        }
      })
  }

  upcoming: EmployeeEventResponseSummary[] = [];

  sort() {
    if (!this.shifts) return;

    const now = new Date();
    this.upcoming = this.shifts.filter(shift => shift.eventStart >= now);
    this.shards = [...new Set(this.shifts.map(s => s.shardName).filter(s => !!s))].sort();

    this.filter();
  }

  // ----- date-range + other filters -----
  // `from`/`to` are the <input type="date"> values: calendar days as "yyyy-MM-dd", read as COMPANY days. They are
  // compared as strings against each shift's company day key, so no Date ever stands in for a day.
  protected from: DayKey | '' = '';
  protected to: DayKey | '' = '';
  protected activePreset: ShiftRangePreset | '' = 'all';

  protected search: string = '';
  protected shardFilter: string | undefined;
  protected shards: string[] = [];

  /** The chips, in display order. */
  protected readonly presets: { id: ShiftRangePreset; label: string }[] = [
    {id: 'all', label: 'Alle'},
    {id: 'lastMonth', label: 'Letzter Monat'},
    {id: 'month', label: 'Dieser Monat'},
    {id: 'week', label: 'Diese Woche'},
    {id: 'today', label: 'Heute'},
    {id: 'upcoming', label: 'Kommende'}
  ];

  private get timeZone(): string {
    return this.companyTimeZone.timeZone();
  }

  protected quickSelect(preset: ShiftRangePreset) {
    this.applyPreset(preset);
    this.filter();
  }

  private applyPreset(preset: ShiftRangePreset) {
    this.activePreset = preset;
    const {from, to} = shiftPresetRange(preset, companyToday(this.timeZone));
    this.from = from;
    this.to = to;
  }

  protected onRangeChanged() {
    this.activePreset = '';
    this.filter();
  }

  protected resetFilters() {
    this.search = '';
    this.shardFilter = undefined;
    this.quickSelect('all');
  }

  protected get hasActiveFilters(): boolean {
    return !!this.search || !!this.shardFilter || !!this.from || !!this.to;
  }

  protected filtered: EmployeeEventResponseSummary[] = [];

  filter() {
    if (!this.shifts) return;

    const timeZone = this.timeZone;
    let result = this.shifts;
    if (this.from || this.to) {
      result = result.filter(s => !!s.eventStart && isInCompanyDayRange(s.eventStart, this.from, this.to, timeZone));
    }
    if (this.shardFilter) result = result.filter(s => s.shardName == this.shardFilter);

    const q = this.search.trim().toLowerCase();
    if (q) {
      result = result.filter(s =>
        (s.eventName ?? '').toLowerCase().includes(q) ||
        (s.locationName ?? '').toLowerCase().includes(q) ||
        (s.shardName ?? '').toLowerCase().includes(q));
    }

    this.filtered = result;
    this.hours = result.reduce((acc, val) => acc + (val.stats?.hoursWorked ?? 0), 0);
  }

  hours: number = 0;
}
